import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiAward, FiUsers, FiHeart, FiTarget } = FiIcons;

const About = () => {
  const team = [
    {
      name: 'Dr. Sarah Johnson',
      title: 'Chief Dentist & Founder',
      image: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=300&h=300&fit=crop',
      experience: '15+ years',
      specialization: 'General & Cosmetic Dentistry'
    },
    {
      name: 'Dr. Michael Chen',
      title: 'Orthodontist',
      image: 'https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=300&h=300&fit=crop',
      experience: '12+ years',
      specialization: 'Orthodontics & Invisalign'
    },
    {
      name: 'Dr. Emily Rodriguez',
      title: 'Pediatric Dentist',
      image: 'https://images.unsplash.com/photo-1594824388853-2c5e2a99c68e?w=300&h=300&fit=crop',
      experience: '10+ years',
      specialization: 'Pediatric Dental Care'
    }
  ];

  const values = [
    {
      icon: FiHeart,
      title: 'Compassionate Care',
      description: 'We treat every patient with kindness, understanding, and genuine care for their comfort and well-being.'
    },
    {
      icon: FiAward,
      title: 'Excellence',
      description: 'We strive for the highest standards in dental care, continuously improving our skills and technology.'
    },
    {
      icon: FiUsers,
      title: 'Patient-Centered',
      description: 'Your needs, comfort, and satisfaction are at the center of everything we do.'
    },
    {
      icon: FiTarget,
      title: 'Precision',
      description: 'We use the latest technology and techniques to provide accurate, effective dental treatments.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>About Us - SmileCare Dental | Expert Dental Team & Modern Facility</title>
        <meta name="description" content="Learn about SmileCare Dental's experienced team of dentists, our modern facility, and our commitment to providing exceptional dental care in a comfortable environment." />
        <meta name="keywords" content="about dentist, dental team, experienced dentists, modern dental clinic, dental care philosophy" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 to-blue-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
            >
              About SmileCare Dental
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl text-gray-600 max-w-3xl mx-auto"
            >
              Dedicated to providing exceptional dental care with a personal touch, combining advanced technology with compassionate service.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Our Story
              </h2>
              <div className="space-y-4 text-gray-600">
                <p>
                  Founded in 2008, SmileCare Dental began with a simple mission: to provide exceptional dental care in a comfortable, welcoming environment. Dr. Sarah Johnson established our practice with the vision of combining cutting-edge dental technology with personalized, compassionate care.
                </p>
                <p>
                  Over the years, we've grown from a small practice to a comprehensive dental clinic serving thousands of patients. Our commitment to excellence has remained unchanged – we believe every patient deserves the highest quality dental care delivered with genuine care and attention.
                </p>
                <p>
                  Today, our team of experienced dentists and dental professionals continues to uphold these values, ensuring that every visit to SmileCare Dental is a positive experience that contributes to your overall health and well-being.
                </p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <img
                src="https://images.unsplash.com/photo-1551601651-2a8555f1a136?w=600&h=400&fit=crop"
                alt="Modern Dental Office Interior"
                className="rounded-lg shadow-lg"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Values Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Values
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The principles that guide everything we do at SmileCare Dental
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white p-6 rounded-lg shadow-lg text-center"
              >
                <div className="bg-blue-600 text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={value.icon} className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-semibold mb-3">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Team Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Meet Our Expert Team
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our experienced dental professionals are committed to providing you with the best possible care
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-lg shadow-lg overflow-hidden text-center"
              >
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-full h-64 object-cover"
                />
                <div className="p-6">
                  <h3 className="text-xl font-semibold mb-2">{member.name}</h3>
                  <p className="text-blue-600 font-medium mb-2">{member.title}</p>
                  <p className="text-gray-600 text-sm mb-2">{member.experience} Experience</p>
                  <p className="text-gray-600 text-sm">{member.specialization}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <img
                src="https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=600&h=400&fit=crop"
                alt="Advanced Dental Technology"
                className="rounded-lg shadow-lg"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Advanced Technology
              </h2>
              <p className="text-gray-600 mb-6">
                We invest in the latest dental technology to provide you with the most effective, comfortable, and efficient treatments available. Our state-of-the-art equipment ensures precise diagnoses and superior treatment outcomes.
              </p>
              <ul className="space-y-3">
                {[
                  'Digital X-rays for reduced radiation exposure',
                  'Intraoral cameras for detailed examinations',
                  'Laser dentistry for minimally invasive procedures',
                  'CAD/CAM technology for same-day crowns',
                  'Digital impressions for comfortable molds'
                ].map((item, index) => (
                  <li key={index} className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};

export default About;